import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GlassCard from "../../../components/common/GlassCard";

function getStatusColor(status) {
  switch (status) {
    case "active":
      return "success";
    case "completed":
      return "default";
    case "on_hold":
      return "warning";
    case "cancelled":
      return "error";
    default:
      return "default";
  }
}

function CarePlansCard({ carePlans, onAddCarePlan, onEditCarePlan, onDeleteCarePlan }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeCarePlanId, setActiveCarePlanId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveCarePlanId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveCarePlanId(null);
  };

  return (
    <GlassCard
      title="Care Plans"
      icon={<AssignmentIcon color="secondary" />}
      onAdd={onAddCarePlan}
      addLabel="Add Care Plan"
    >
      {carePlans?.length ? (
        carePlans.map((plan) => (
          <Box
            key={plan.id}
            mb={1}
            sx={{
              p: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
            data-testid={`care-plan-item-${plan.id}`}
            data-care-plan-status={plan.status}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box flex={1}>
                <Typography variant="body2" fontWeight={500}>
                  {plan.title}
                </Typography>
                {plan.description && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {plan.description.substring(0, 60)}
                    {plan.description.length > 60 ? "..." : ""}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block">
                  Start: {plan.start_date ? new Date(plan.start_date).toLocaleDateString() : "N/A"}
                </Typography>
                {plan.next_review_date && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Next Review: {new Date(plan.next_review_date).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              <Box display="flex" gap={0.5} alignItems="center">
                <Chip 
                  label={plan.status} 
                  size="small" 
                  color={getStatusColor(plan.status)} 
                  variant="outlined"
                  data-testid={`care-plan-status-chip-${plan.id}`}
                  aria-label={`Care plan status: ${plan.status}`}
                />
                <Tooltip title="Options">
                  <IconButton 
                    size="small" 
                    onClick={(e) => handleMenuOpen(e, plan.id)}
                    data-testid={`care-plan-menu-button-${plan.id}`}
                    aria-label="Open care plan options"
                    data-action="care-plan-menu"
                    data-care-plan-id={plan.id}
                  >
                    <MoreVertIcon 
                      aria-label="Open care plan options" 
                      fontSize="small"
                      data-testid={`care-plan-options-icon-${plan.id}`}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No care plans
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        {onEditCarePlan && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              if (activeCarePlanId) {
                const plan = carePlans.find(p => p.id === activeCarePlanId);
                if (plan) onEditCarePlan(plan);
              }
            }}
            data-testid={`care-plan-edit-menu-item-${activeCarePlanId}`}
            aria-label="Edit care plan"
            data-action="edit-care-plan"
            data-care-plan-id={activeCarePlanId}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
        )}
        {onDeleteCarePlan && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              if (activeCarePlanId) {
                onDeleteCarePlan(activeCarePlanId);
              }
            }}
            data-testid={`care-plan-delete-menu-item-${activeCarePlanId}`}
            aria-label="Delete care plan"
            data-action="delete-care-plan"
            data-care-plan-id={activeCarePlanId}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>
    </GlassCard>
  );
}

CarePlansCard.propTypes = {
  carePlans: PropTypes.array,
  onAddCarePlan: PropTypes.func.isRequired,
  onEditCarePlan: PropTypes.func,
  onDeleteCarePlan: PropTypes.func,
};

CarePlansCard.defaultProps = {
  carePlans: [],
};

export default CarePlansCard;
